export const mutations = {
  // Create a new chat session
  CREATE_SESSION(state, { orgId, session }) {
    if (!state.sessions[orgId]) {
      state.sessions[orgId] = {}
    }
    state.sessions[orgId][session.session_code] = session

    // Only initialize messages array if it doesn't exist
    // This prevents clearing messages when re-fetching a session
    if (!state.messages[session.session_code]) {
      state.messages[session.session_code] = []
    }
  },

  // Set active session
  SET_ACTIVE_SESSION(state, sessionCode) {
    state.activeSessionCode = sessionCode
  },

  // Delete a session
  DELETE_SESSION(state, { orgId, sessionCode }) {
    if (state.sessions[orgId]) {
      delete state.sessions[orgId][sessionCode]
    }
    delete state.messages[sessionCode]

    if (state.activeSessionCode === sessionCode) {
      state.activeSessionCode = null
    }
  },

  // Add a message to a session
  ADD_MESSAGE(state, { sessionCode, message }) {
    if (!state.messages[sessionCode]) {
      state.messages[sessionCode] = []
    }

    // Check if message already exists (by message_uuid or message_code)
    const exists = state.messages[sessionCode].some(m =>
      m.message_uuid === message.message_uuid ||
      m.message_code === message.message_code
    )

    // Only add if it doesn't exist (prevents duplicates from WebSocket + API)
    if (!exists) {
      state.messages[sessionCode].push(message)
    }
  },

  // Update a message
  UPDATE_MESSAGE(state, { sessionCode, messageCode, updates }) {
    const messages = state.messages[sessionCode]
    if (messages) {
      const index = messages.findIndex(m => m.message_code === messageCode)
      if (index !== -1) {
        messages[index] = { ...messages[index], ...updates }
      }
    }
  },

  // Delete a message
  DELETE_MESSAGE(state, { sessionCode, messageCode }) {
    const messages = state.messages[sessionCode]
    if (messages) {
      const index = messages.findIndex(m => m.message_code === messageCode)
      if (index !== -1) {
        messages.splice(index, 1)
      }
    }
  },

  // Set all messages for a session
  SET_MESSAGES(state, { sessionCode, messages }) {
    state.messages[sessionCode] = messages
  },

  // Clear all chat data
  CLEAR_CHAT_DATA(state) {
    state.sessions = {}
    state.messages = {}
    state.activeSessionCode = null
    state.prompts = {}
  },

  // ==================== Prompt Mutations ====================

  // Set all prompts for a session
  SET_SESSION_PROMPTS(state, { sessionCode, prompts }) {
    state.prompts[sessionCode] = prompts
  },

  // Add a prompt link to a session
  ADD_PROMPT_LINK(state, { sessionCode, entityType, prompt }) {
    if (!state.prompts[sessionCode]) {
      state.prompts[sessionCode] = {
        organization: null,
        system: null,
        projects: [],
        layers: []
      }
    }

    if (entityType === 'project') {
      // Check if already exists
      const exists = state.prompts[sessionCode].projects.some(p => p.id === prompt.id)
      if (!exists) {
        state.prompts[sessionCode].projects.push(prompt)
      }
    } else if (entityType === 'layer') {
      const exists = state.prompts[sessionCode].layers.some(p => p.id === prompt.id)
      if (!exists) {
        state.prompts[sessionCode].layers.push(prompt)
      }
    }
  },

  // Remove a prompt link from a session
  REMOVE_PROMPT_LINK(state, { sessionCode, entityType, promptId }) {
    if (!state.prompts[sessionCode]) return

    if (entityType === 'project') {
      state.prompts[sessionCode].projects = state.prompts[sessionCode].projects.filter(
        p => p.id !== promptId
      )
    } else if (entityType === 'layer') {
      state.prompts[sessionCode].layers = state.prompts[sessionCode].layers.filter(
        p => p.id !== promptId
      )
    }
  },

  // Update prompt content (updates across all sessions using it)
  UPDATE_PROMPT_CONTENT(state, { promptId, newContent }) {
    // Update in all sessions that have this prompt
    Object.keys(state.prompts).forEach(sessionCode => {
      const sessionPrompts = state.prompts[sessionCode]

      // Update organization prompt
      if (sessionPrompts.organization?.id === promptId) {
        sessionPrompts.organization.prompt = newContent
      }

      // Update system prompt
      if (sessionPrompts.system?.id === promptId) {
        sessionPrompts.system.prompt = newContent
      }

      // Update in projects
      sessionPrompts.projects = sessionPrompts.projects.map(p =>
        p.id === promptId ? { ...p, prompt: newContent } : p
      )

      // Update in layers
      sessionPrompts.layers = sessionPrompts.layers.map(p =>
        p.id === promptId ? { ...p, prompt: newContent } : p
      )
    })
  },

  // Set available models
  SET_AVAILABLE_MODELS(state, models) {
    state.availableModels = models
  }
}
